import {
  DeleteButton,
  EmailField,
  FilterDropdown,
  List,
  ShowButton,
  TextField,
  useTable,
} from "@refinedev/antd";
import { BaseRecord, HttpError, useTranslate } from "@refinedev/core";
import { Input, Space, Table, Avatar, Typography, Button, Tooltip, notification } from "antd";
import { AccountType } from "../../models/account";
import { useQontoActive } from "./hooks/useQontoActive";
import { GlassCard } from "../../shared/components";
import { AccountStatusLabels } from "../orders/components/accountStatusLabels";
import { useAvatarPalette } from "../../shared/styles";
import { getAvatarColor, getInitialsFromName } from "../../shared/utils";
import { useState } from "react";
import { AccountEditModal } from "./components/AccountEditModal";
import { AccountCreateModal } from "./components/AccountCreateModal";
import { AccountQontoRetryButton } from "./components/AccountQontoRetryButton";
import { AddCreditModal } from "./components/AddCreditModal";
import { InviteUserModal } from "./show/InviteUserModal";
import {
  EditOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
  UserAddOutlined,
  WalletOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

interface IAccount {
  id: number;
  name: string;
  type?: AccountType;
  billingMode?: string;
  credit?: number;
  qontoClientId?: string | null;
  qontoSyncError?: boolean;
  qontoSyncErrorMessage?: string | null;
}

export const AccountList = () => {
  const _t = useTranslate();
  const avatarPalette = useAvatarPalette();
  const qontoActive = useQontoActive();

  const { tableProps, tableQueryResult } = useTable<IAccount, HttpError>({
    syncWithLocation: true,
    sorters: {
      initial: [{ field: "id", order: "asc" }],
    },
    filters: {
      initial: [
        {
          field: "name",
          operator: "contains",
          value: "",
        },
      ],
    },
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState<number | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [addCreditModalOpen, setAddCreditModalOpen] = useState(false);
  const [addCreditAccount, setAddCreditAccount] = useState<IAccount | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [invitingAccount, setInvitingAccount] = useState<IAccount | null>(null);

  const openInviteModal = (account: IAccount) => {
    setInvitingAccount(account);
    setInviteModalOpen(true);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <List
        headerButtons={({ createButtonProps: _ }) => [
          <Button
            key="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            {_t("accounts.titles.create")}
          </Button>,
        ]}
      >
        <style>{`
        .accounts-list-table-view .ant-table { background: transparent !important; }
        .accounts-list-table-view .ant-table-thead > tr > th { background: transparent !important; }
        .accounts-list-table-view .ant-table-tbody > tr > td { background: transparent !important; }
      `}</style>
        <GlassCard className="accounts-list-table-view !p-0 overflow-hidden">
          <Table {...tableProps} rowKey="id">
            <Table.Column
              title={_t("accounts.fields.name")}
              key="account"
              width={300}
              sorter={{ multiple: 1 }}
              filterDropdown={(props) => (
                <FilterDropdown {...props}>
                  <Input placeholder={_t("accounts.fields.name")} />
                </FilterDropdown>
              )}
              render={(_, record: any) => (
                <Space>
                  <Avatar
                    shape="square"
                    size={44}
                    style={{
                      backgroundColor: getAvatarColor(record.id, avatarPalette),
                      fontWeight: 600,
                    }}
                  >
                    {getInitialsFromName(record.name)}
                  </Avatar>
                  <div>
                    <Space size={4} align="center">
                      <Text strong>{record.name}</Text>
                      {record.qontoSyncError && (
                          <>
                            <Tooltip
                              title={
                                record.qontoSyncErrorMessage ||
                                _t("accounts.qontoSyncErrorHint")
                              }
                            >
                              <ExclamationCircleFilled
                                className="text-red-500 text-sm"
                                aria-label={_t("accounts.qontoSyncError")}
                              />
                            </Tooltip>
                            <AccountQontoRetryButton
                              accountId={record.id}
                              showLabel={false}
                              onSynced={() => void tableQueryResult?.refetch()}
                            />
                          </>
                        )}
                      {qontoActive &&
                        !record.qontoClientId &&
                        !record.qontoSyncError && (
                          <>
                            <Tooltip title={_t("accounts.qontoNotSyncedHint")}>
                              <ExclamationCircleFilled
                                className="text-amber-500 text-sm"
                                aria-label={_t("accounts.qontoNotSynced")}
                              />
                            </Tooltip>
                            <AccountQontoRetryButton
                              accountId={record.id}
                              showLabel={false}
                              onSynced={() => void tableQueryResult?.refetch()}
                            />
                          </>
                        )}
                    </Space>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      #{record.id}
                    </Text>
                  </div>
                </Space>
              )}
            />
            <Table.Column
              dataIndex="type"
              title={_t("accounts.fields.type")}
              render={(value: any) => (
                <TextField value={_t(`accounts.fields.typeValues.${value}`)} />
              )}
            />
            <Table.Column
              dataIndex="phone"
              title={_t("accounts.fields.phone")}
            />
            <Table.Column
              dataIndex={["email"]}
              title={_t("accounts.fields.email")}
              render={(value: any) => <EmailField value={value} copyable />}
            />
            <Table.Column
              dataIndex="billingMode"
              title={_t("accounts.fields.billingMode")}
              render={(value: any) => (
                <TextField
                  value={_t(`accounts.fields.billingModeValues.${value}`)}
                />
              )}
            />
            <Table.Column
              dataIndex="credit"
              title={_t("accounts.fields.credit")}
              render={(value: number | undefined, record: IAccount) =>
                record.billingMode === "prepaid" ? (
                  <TextField value={value ?? 0} />
                ) : (
                  <Text type="secondary">—</Text>
                )
              }
            />

            <Table.Column
              dataIndex="status"
              title={_t("accounts.fields.status")}
              render={(value: any) => <AccountStatusLabels value={value} />}
              width={100}
              filters={[
                {
                  text: _t("accounts.fields.statusValues.draft"),
                  value: "draft",
                },
                {
                  text: _t("accounts.fields.statusValues.active"),
                  value: "active",
                },
                {
                  text: _t("accounts.fields.statusValues.inactive"),
                  value: "inactive",
                },
                {
                  text: _t("accounts.fields.statusValues.suspended"),
                  value: "suspended",
                },
              ]}
              onFilter={(value: any, record: any) => record.status === value}
            />

            <Table.Column
              title={_t("table.actions")}
              dataIndex="actions"
              render={(_, record: BaseRecord & IAccount) => (
                <Space>
                  <Tooltip title={_t("accounts.actions.inviteUser")}>
                    <Button
                      size="small"
                      icon={<UserAddOutlined />}
                      aria-label={_t("accounts.actions.inviteUser")}
                      onClick={() => openInviteModal(record)}
                    />
                  </Tooltip>
                  {record.billingMode === "prepaid" && (
                    <Tooltip title={_t("accounts.actions.addCredit")}>
                      <Button
                        size="small"
                        icon={<WalletOutlined />}
                        aria-label={_t("accounts.actions.addCredit")}
                        onClick={() => {
                          setAddCreditAccount(record);
                          setAddCreditModalOpen(true);
                        }}
                      />
                    </Tooltip>
                  )}
                  <Button
                    size="small"
                    onClick={() => {
                      setEditingAccountId(record.id as number);
                      setEditModalOpen(true);
                    }}
                  >
                    <EditOutlined />
                  </Button>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton
                    hideText
                    size="small"
                    recordItemId={record.id}
                  />
                </Space>
              )}
            />
          </Table>
        </GlassCard>
        <AccountEditModal
          open={editModalOpen}
          accountId={editingAccountId}
          onCancel={() => {
            setEditModalOpen(false);
            setEditingAccountId(null);
          }}
          onSuccess={() => {
            tableQueryResult?.refetch();
          }}
        />
        <AccountCreateModal
          open={createModalOpen}
          onCancel={() => setCreateModalOpen(false)}
          onSuccess={(account) => {
            tableQueryResult?.refetch();
            if (!account?.id) {
              return;
            }
            const key = `invite-after-create-${account.id}`;
            notification.open({
              key,
              message: _t("accounts.messages.createdTitle"),
              description: _t("accounts.messages.createdInviteQuestion"),
              duration: 8,
              btn: (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    notification.destroy(key);
                    openInviteModal({
                      id: account.id,
                      name: account.name,
                    });
                  }}
                >
                  {_t("accounts.actions.inviteUser")}
                </Button>
              ),
            });
          }}
        />
        <AddCreditModal
          open={addCreditModalOpen}
          accountId={addCreditAccount?.id ?? null}
          accountName={addCreditAccount?.name}
          currentCredit={addCreditAccount?.credit ?? 0}
          onCancel={() => {
            setAddCreditModalOpen(false);
            setAddCreditAccount(null);
          }}
          onSuccess={() => {
            tableQueryResult?.refetch();
          }}
        />
        <InviteUserModal
          open={inviteModalOpen}
          accountId={invitingAccount?.id}
          accountName={invitingAccount?.name}
          onCancel={() => {
            setInviteModalOpen(false);
            setInvitingAccount(null);
          }}
          onSuccess={() => {
            setInviteModalOpen(false);
            setInvitingAccount(null);
          }}
        />
      </List>
    </div>
  );
};
